// src/game/ReplayRecorder.js
// Records gameplay events for replay — zero performance cost (just array pushes).
// Attached to LevelScene during create(), hooks fire at swap/cascade/victory points.
// Infinite mode uses a rolling buffer (last N turns) to keep replays short.

const MAX_INFINITE_TURNS = 15; // keep only the last 15 turns in infinite mode

export class ReplayRecorder {
  constructor() {
    this.reset();
  }

  reset() {
    this.data = null;
    this._currentTurn = null;
    this._turnIndex = 0;
    this._recording = false;
    this._infinite = false;
  }

  // ── Called once at level start after board is generated ──────────────────
  startLevel({ levelId, regionId, movesLeft, goals, board, infinite }) {
    this._recording = true;
    this._infinite = !!infinite;
    this._turnIndex = 0;
    this.data = {
      levelId,
      regionId,
      movesLeft,
      infinite: this._infinite,
      goals: goals ? JSON.parse(JSON.stringify(goals)) : [],
      initialBoard: this._snapshotBoard(board),
      turns: [],
      result: null,
      timestamp: Date.now(),
    };
  }

  // ── Called before each swap animation ───────────────────────────────────
  recordSwap(from, to) {
    if (!this._recording) return;
    this._turnIndex++;
    this._currentTurn = {
      turn: this._turnIndex,
      swap: { from: { r: from.r, c: from.c }, to: { r: to.r, c: to.c } },
      cascades: [],
      scoreAfter: 0,
      movesAfter: 0,
      t: Date.now() - this.data.timestamp,
    };
  }

  // ── Called after each cascade iteration (match found + removed + refilled) ──
  recordCascade({ matched, comboMultiplier, scoreGained, board }) {
    if (!this._recording || !this._currentTurn) return;
    this._currentTurn.cascades.push({
      matched: matched.map(p => ({ r: p.r, c: p.c })),
      comboMultiplier,
      scoreGained,
      boardAfter: this._snapshotBoard(board),
    });
  }

  // ── Called after full swap resolution (all cascades done) ──────────────
  finishTurn({ score, movesLeft }) {
    if (!this._recording || !this._currentTurn) return;
    this._currentTurn.scoreAfter = score;
    this._currentTurn.movesAfter = movesLeft;
    this.data.turns.push(this._currentTurn);
    this._currentTurn = null;

    // Rolling buffer for infinite mode — keep only the last N turns
    if (this._infinite && this.data.turns.length > MAX_INFINITE_TURNS) {
      // Before trimming, update initialBoard to the board state at the start
      // of the oldest remaining turn (= boardAfter of the turn we're dropping)
      const dropped = this.data.turns.shift();
      const lastCascade = dropped.cascades[dropped.cascades.length - 1];
      if (lastCascade?.boardAfter) {
        this.data.initialBoard = lastCascade.boardAfter;
      }
    }
  }

  // ── Called on invalid swap (swapped back) — discard the pending turn ────
  discardTurn() {
    if (!this._recording) return;
    this._currentTurn = null;
    this._turnIndex--;
  }

  // ── Called on victory ──────────────────────────────────────────────────
  recordVictory({ score, stars, maxCombo, timeBonus }) {
    if (!this._recording) return;
    this.data.result = {
      won: true,
      score,
      stars,
      maxCombo,
      timeBonus,
      duration: Date.now() - this.data.timestamp,
    };
    this._recording = false;
  }

  // ── Called on infinite game over ───────────────────────────────────────
  recordInfiniteEnd({ score, wave, maxCombo }) {
    if (!this._recording) return;
    this.data.result = {
      won: true, // treat as "won" so hasReplay() returns true
      infinite: true,
      score,
      wave,
      maxCombo,
      stars: 0,
      timeBonus: 0,
      duration: Date.now() - this.data.timestamp,
    };
    this._recording = false;
  }

  // ── Called on defeat ───────────────────────────────────────────────────
  recordDefeat({ score }) {
    if (!this._recording) return;
    this.data.result = {
      won: false,
      score,
      duration: Date.now() - this.data.timestamp,
    };
    this._recording = false;
  }

  // ── Board snapshot — 8×8 array of color strings ────────────────────────
  _snapshotBoard(board) {
    const grid = board.grid;
    const snap = [];
    for (let r = 0; r < grid.length; r++) {
      const row = [];
      for (let c = 0; c < grid[r].length; c++) {
        row.push(grid[r][c]?.color || null);
      }
      snap.push(row);
    }
    return snap;
  }

  // ── Check if we have a valid recording ─────────────────────────────────
  hasReplay() {
    return this.data && this.data.result?.won && this.data.turns.length > 0;
  }

  // ── Get the recorded data (immutable copy) ─────────────────────────────
  getReplayData() {
    return this.data ? JSON.parse(JSON.stringify(this.data)) : null;
  }
}
